import React, { useState } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Divider } from "@amzn/meridian";
import Loader from "@amzn/meridian/loader";
import { ClassifyWasteProfileResponse } from "src/types/classificationTypes";
import { initiateAiExternalResearch } from "src/services/classificationService";
import { useAppSelector } from "src/store/store";
import aiResearchMascot from "src/images/ai-research-mascot.png";
import "./classification.scss";

interface AiResearchCardProps {
  asin: string;
  itemName?: string;
  classificationId: string;
  onViewResults: (result: ClassifyWasteProfileResponse) => void;
}

function AiResearchCard({ asin, itemName, classificationId, onViewResults }: AiResearchCardProps) {
  const { activeSiteCode } = useAppSelector((state) => state.user);
  const [result, setResult] = useState<ClassifyWasteProfileResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const runResearch = async () => {
    setIsLoading(true);
    setError(false);
    try {
      const res = await initiateAiExternalResearch({
        itemName: itemName ?? "",
        asin,
        siteName: activeSiteCode,
        classificationId,
      });
      setResult(res);
    } catch (e) {
      console.error("[AiResearchCard] AI external research failed:", e);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Completed: one of three states — document found, web attributes found, or no result.
  if (result) {
    if (result.recommendedWasteProfilesIds.length === 0) {
      return (
        <Column className="home-card ai-research-card" spacingInset="400" spacing="200">
          <Text type="h200">AI Analysis completed</Text>
          <Text type="b300" color="secondary">
            {"The system couldn't find any additional results related to this product from either internal or external sources."}
          </Text>
        </Column>
      );
    }
    const isWeb = result.sdsSource === "WEB";
    const externalSourcesAnalyzed = result.externalSourcesAnalyzed ?? 0;
    const attributesExtracted = result.sdsAttributes?.length ?? 0;
    return (
      <Column className="home-card ai-research-card" spacingInset="400" spacing="400">
        <Text type="h200">AI Analysis completed</Text>
        {isWeb ? (
          <Row spacing="500">
            <Column spacing="none">
              <Text type="h400">{externalSourcesAnalyzed}</Text>
              <Text type="b300" color="secondary">
                External sources analysed
              </Text>
            </Column>
            <Column spacing="none">
              <Text type="h400">{attributesExtracted}</Text>
              <Text type="b300" color="secondary">
                {attributesExtracted === 1 ? "Attribute extracted" : "Attributes extracted"}
              </Text>
            </Column>
          </Row>
        ) : result.sdsSource === "DOCUMENT" && result.sdsDocument ? (
          <Column spacing="none">
            <Text type="h400">1</Text>
            <Text type="b300" color="secondary">
              SDS Document found
            </Text>
          </Column>
        ) : null}
        <button className="ai-external-search-btn" onClick={() => onViewResults(result)}>
          VIEW RESULTS
        </button>
      </Column>
    );
  }

  // In progress.
  if (isLoading) {
    return (
      <Column className="home-card ai-research-card" spacingInset="400" spacing="300">
        <Row alignmentVertical="top" alignmentHorizontal="justify">
          <Column spacing="200">
            <Text type="h200">AI Assisted research in progress</Text>
            <Text type="b300" color="secondary">
              We&apos;re analyzing external sources to determine this product&apos;s classification.
            </Text>
          </Column>
          <Loader size="medium" type="circular" />
        </Row>
        <Divider />
        <Text type="b200" color="secondary">
          Please stay on this product and wait for the research to complete before continuing.
        </Text>
      </Column>
    );
  }

  return (
    <Column className="home-card ai-research-card" spacing="300" spacingInset="400">
      <Row alignmentVertical="center" spacing="300">
        <Text type="b300" color="secondary">
          Not sure about the classification yet? Try our AI-Assisted feature to make a quick
          decision.
        </Text>
        <img className="ai-research-promo__img" src={aiResearchMascot} alt="AI-Assisted research" />
      </Row>
      <button className="ai-external-search-btn" onClick={runResearch}>
        INITIATE AI EXTERNAL SEARCH
      </button>
      {error && (
        <Text type="b200" color="error">
          AI research failed. Please try again.
        </Text>
      )}
    </Column>
  );
}

export default AiResearchCard;
